// Sign-in and sign-out flows, replacing the SDK's `authWithPassword` /
// `authWithOAuth2` / client-only logout.
//
// The OAuth2 flow is a hand-rolled copy of the SDK's realtime popup flow and is
// wire-identical to it: same auth-methods lookup, same `/api/oauth2-redirect`
// redirect URL (so the Entra app registration needs NO change), same realtime
// `@oauth2` subscription keyed by the connection's clientId, same code exchange.
// Changing any of these is a behavior change that belongs in its own PR.
import { ApiError, api, apiUrl } from "./api";
import { type AuthRecord, authStore } from "./auth-store";

type AuthResponse = { token: string; record: AuthRecord };

/** Password sign-in. Saves the session on success; throws ApiError on failure. */
export async function loginWithPassword(email: string, password: string): Promise<AuthResponse> {
  const auth = await api<AuthResponse>("/api/collections/users/auth-with-password", {
    method: "POST",
    body: { identity: email, password },
  });
  authStore.save(auth.token, auth.record);
  return auth;
}

/**
 * Logs the user out. Asks the server to rotate the account's token key
 * (invalidating every outstanding JWT — pentest FRY-5) before clearing the
 * local auth store. The local clear runs even if the server call fails, so a
 * network error never traps the user in a logged-in UI; a best-effort server
 * revocation is strictly better than a client-only logout.
 */
export async function logout() {
  try {
    await api("/app/auth/logout", { method: "POST" });
  } catch (error) {
    console.warn("Server-side logout failed; clearing local session anyway:", error);
  } finally {
    authStore.clear();
  }
}

type OAuth2Provider = {
  name: string;
  state: string;
  codeVerifier: string;
  authURL: string;
};

type AuthMethods = {
  oauth2?: { providers?: OAuth2Provider[] };
};

/**
 * Microsoft SSO via the OAuth2 popup flow.
 *
 * Must be called synchronously from a click handler: the popup is opened
 * immediately (to about:blank) so the browser attributes it to the user
 * gesture, then navigated once the realtime connection has a clientId.
 *
 * How the code gets back without a registered app redirect URI: PocketBase
 * sends `state=<realtime clientId>` to the provider, the provider redirects to
 * PocketBase's own `/api/oauth2-redirect`, and that handler broadcasts
 * `{state, code}` over the realtime connection whose id matches — which is the
 * one opened here. The popup never talks to the app at all.
 */
export function loginWithMicrosoft(): Promise<AuthResponse> {
  const popup = openPopup();

  let eventSource: EventSource | null = null;
  function cleanup() {
    eventSource?.close();
    eventSource = null;
    try {
      popup?.close();
    } catch {
      // a popup on another origin may refuse scripting; it closes itself anyway
    }
  }

  return new Promise<AuthResponse>((resolve, reject) => {
    let settled = false;
    const fail = (error: unknown) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(
        error instanceof ApiError ? error : new ApiError(0, error instanceof Error ? error.message : String(error)),
      );
    };

    (async () => {
      const methods = await api<AuthMethods>("/api/collections/users/auth-methods");
      const provider = methods.oauth2?.providers?.find((p) => p.name === "microsoft");
      if (!provider) throw new ApiError(0, 'Missing or invalid provider "microsoft".');
      const redirectUrl = apiUrl("/api/oauth2-redirect");

      const es = new EventSource(apiUrl("/api/realtime"));
      eventSource = es;
      let clientId = "";

      es.addEventListener("PB_CONNECT", (event) => {
        // EventSource auto-reconnects with a NEW clientId, but the provider was
        // already handed the old one as `state` — a late PB_CONNECT can only
        // mean the broadcast will never reach us, so treat it as a dead flow.
        if (clientId !== "") {
          fail(new ApiError(0, "realtime connection interrupted"));
          return;
        }
        clientId = (event as MessageEvent).lastEventId;
        (async () => {
          await api("/api/realtime", {
            method: "POST",
            body: { clientId, subscriptions: ["@oauth2"] },
          });
          if (settled || !popup) return;
          popup.location.href = replaceQueryParams(provider.authURL + redirectUrl, { state: clientId });
        })().catch(fail);
      });

      es.addEventListener("@oauth2", (event) => {
        (async () => {
          const data = JSON.parse((event as MessageEvent).data) as {
            state?: string;
            code?: string;
            error?: string;
          };
          if (!data.state || data.state !== clientId) throw new Error("State parameters don't match.");
          if (data.error || !data.code) throw new Error(`OAuth2 redirect error or missing code: ${data.error}`);
          const auth = await api<AuthResponse>("/api/collections/users/auth-with-oauth2", {
            method: "POST",
            body: {
              provider: provider.name,
              code: data.code,
              codeVerifier: provider.codeVerifier,
              redirectURL: redirectUrl,
            },
          });
          authStore.save(auth.token, auth.record);
          if (settled) return;
          settled = true;
          cleanup();
          resolve(auth);
        })().catch(fail);
      });

      es.onerror = () => {
        // Fires on connect failure and on connection loss. Before PB_CONNECT the
        // flow never started; after it, see above — either way it cannot finish.
        fail(new ApiError(0, "realtime connection interrupted"));
      };
    })().catch(fail);
  });
}

function openPopup(): Window | null {
  if (typeof window === "undefined" || !window.open) return null;
  let width = 1024;
  let height = 768;
  const windowWidth = window.innerWidth;
  const windowHeight = window.innerHeight;
  width = width > windowWidth ? windowWidth : width;
  height = height > windowHeight ? windowHeight : height;
  const left = windowWidth / 2 - width / 2;
  const top = windowHeight / 2 - height / 2;
  return window.open(
    undefined,
    "popup_window",
    `width=${width},height=${height},top=${top},left=${left},resizable,menubar=no`,
  );
}

// The SDK's _replaceQueryParams, ported as-is: PocketBase's authURL ends with
// `redirect_uri=` so the redirect URL is appended by concatenation BEFORE this
// runs, and `state` must then be replaced with the realtime clientId.
function replaceQueryParams(url: string, replacements: Record<string, string>): string {
  let urlPath = url;
  let query = "";
  const queryIndex = url.indexOf("?");
  if (queryIndex >= 0) {
    urlPath = url.substring(0, queryIndex);
    query = url.substring(queryIndex + 1);
  }

  const params: Record<string, string> = {};
  for (const part of query.split("&")) {
    if (part === "") continue;
    const pair = part.split("=");
    params[decodeURIComponent(pair[0].replace(/\+/g, " "))] = decodeURIComponent((pair[1] || "").replace(/\+/g, " "));
  }
  for (const [key, value] of Object.entries(replacements)) {
    params[key] = value;
  }

  let rebuilt = "";
  for (const [key, value] of Object.entries(params)) {
    if (rebuilt !== "") rebuilt += "&";
    rebuilt += `${encodeURIComponent(key.replace(/%20/g, "+"))}=${encodeURIComponent(value.replace(/%20/g, "+"))}`;
  }
  return rebuilt !== "" ? `${urlPath}?${rebuilt}` : urlPath;
}
